import { ActivatedRouteSnapshot, CanActivateFn, GuardResult, MaybeAsync, Router } from "@angular/router";
import { inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AuthService, CommonPortalUrlService, UrlParamService, UserAuthorizedService } from "../services";
import { TranslationService } from "gast-common-ng2";
import { ToastrService } from "ngx-toastr";
import { AuthenticationDto } from "../dto";
import { catchError, map, of } from "rxjs";

export const portalAuthAccessGuard: CanActivateFn = (route: ActivatedRouteSnapshot): MaybeAsync<GuardResult> => {
  const httpClient = inject(HttpClient);
  const commonUrlService = inject(CommonPortalUrlService);
  const authService = inject(AuthService);
  const userAuthService = inject(UserAuthorizedService);
  const urlParamService = inject(UrlParamService);
  const translationService = inject(TranslationService);
  const toastService = inject(ToastrService);
  const router = inject(Router);

  const cleanUpUserState: () => void = () => {
    toastService.error(
      translationService.translateKey("GAST-PORTAL-NG.AUTH_GUARD.UNAUTHORIZED_ERROR") || "Unable to authenticate",
      translationService.translateKey("GAST-PORTAL-NG.TOASTER-HEADERS.ERROR") || ""
    );
    authService.saveUserObjectInStorage("", "");
    userAuthService.updateAuthorizedUser(null);
  };

  urlParamService.saveSessionFromParamAndRemoveParam(route.queryParamMap);
  return httpClient.get<AuthenticationDto>(commonUrlService.getUserInfo()).pipe(
    map((userDto: AuthenticationDto) => {
      if (userDto && userAuthService.isAuthenticated(userDto)) {
        if (userAuthService.isAuthorized(userDto)) {
          authService.saveUserObjectInStorage(userDto.name, userDto.principal.longusername);
          userAuthService.updateAuthorizedUser({
            userId: userDto.principal.userId,
            userName: userDto.name,
            personId: userDto.principal.personId,
            organizationId: userDto.principal.organizationId,
          });
          return true;
        } else {
          // if the user could log in but still has authority to get into the view he should be redirected
          // to the login page to change the account but without redirect.
          // If the redirect will be in url the redirect loop occurs due the uses session is
          // valid and will be instantly redirected back here.
          cleanUpUserState();
          userAuthService.logout();
          return false;
        }
      } else {
        cleanUpUserState();
        userAuthService.login();
        return false;
      }
    }),
    catchError(() => {
      toastService.error(
        translationService.translateKey("GAST-PORTAL-NG.AUTH_GUARD.UNKNOWN_ERROR") ||
          "Request for user authorization failed",
        translationService.translateKey("GAST-PORTAL-NG.TOASTER-HEADERS.ERROR") || ""
      );
      return of(router.parseUrl("/something-went-wrong"));
    })
  );
};
